/**
 * Pressure Correction — Step 2 of SIMPLE.
 *
 * Enforces mass conservation by solving for pressure correction P':
 *
 *   a_P · P'_P = Σ(a_nb · P'_nb) + b_P
 *
 * where b_P = -Σ(mdot*_f) is the mass imbalance from the momentum predictor.
 *
 * The corrected values are:
 *   P = P* + α_P · P'
 *   mdot = mdot* - d_f · (P'_R - P'_L)
 */

import type { PipeSegment } from '../types';
import { tdma } from './linearSolver';

/**
 * Solve pressure correction for a single pipe and apply corrections.
 *
 * @param pipe The pipe segment
 * @param d_f Pressure-velocity coupling coefficients from momentum predictor
 * @param alpha_P Pressure under-relaxation factor (0-1)
 * @param P_left Boundary pressure at inlet face [Pa] (null = use mass balance)
 * @param P_right Boundary pressure at outlet face [Pa] (null = use mass balance)
 * @returns Maximum mass residual for this pipe
 */
export function pressureCorrectionStep(
  pipe: PipeSegment,
  d_f: number[],
  alpha_P: number = 0.3,
  P_left: number | null = null,
  P_right: number | null = null,
): number {
  const { nCells, dx, area } = pipe;
  const n = nCells;

  // Build tridiagonal system for P'
  const a_sub = new Array(n).fill(0);   // sub-diagonal
  const a_main = new Array(n).fill(0);  // main diagonal
  const a_sup = new Array(n).fill(0);   // super-diagonal
  const rhs = new Array(n).fill(0);     // right-hand side

  let maxResidual = 0;

  for (let i = 0; i < n; i++) {
    // Mass imbalance at cell i: sum of face mass flows (inflow positive)
    const mdot_in = pipe.faces[i].mdot;     // left face (inflow)
    const mdot_out = pipe.faces[i + 1].mdot; // right face (outflow)

    // Mass residual: should be zero for incompressible steady-state
    // For transient: include ρ·V/dt term
    const massResidual = mdot_in - mdot_out;

    // Track maximum residual
    maxResidual = Math.max(maxResidual, Math.abs(massResidual));

    // Coefficients for P' equation
    const d_left = d_f[i];
    const d_right = d_f[i + 1];

    // Left neighbor coefficient
    if (i > 0) {
      a_sub[i] = -d_left;
    }

    // Right neighbor coefficient
    if (i < n - 1) {
      a_sup[i] = -d_right;
    }

    // Main diagonal
    a_main[i] = d_left + d_right;

    // RHS = mass imbalance
    rhs[i] = massResidual;

    // Handle fixed-pressure boundaries: enforce P' = 0 at boundary.
    // The boundary pressure is already correct, so no correction is needed.
    // We add d_boundary to the diagonal (treating the boundary neighbor as having P'=0),
    // which is equivalent to removing the boundary face from the correction equation.
    if (i === 0 && P_left !== null) {
      // Boundary face contribution: a_sub would connect to P'_left = 0 (no correction).
      // The left face mass flow correction is: mdot'_left = -d_left * (P'[0] - P'_left)
      // Since P'_left = 0: mdot'_left = -d_left * P'[0], already in a_main via d_left.
      // No additional diagonal adjustment needed — d_left is already in a_main[i].
    }
    if (i === n - 1 && P_right !== null) {
      // Same logic: P'_right = 0, d_right is already in a_main[i].
    }
  }

  // Solve for P'
  const P_prime = tdma(a_sub, a_main, a_sup, rhs);

  // Apply pressure correction with under-relaxation (skip NaN)
  for (let i = 0; i < n; i++) {
    if (isFinite(P_prime[i])) {
      pipe.cells[i].P += alpha_P * P_prime[i];
    }
  }

  // Correct face mass flow rates
  for (let f = 1; f < n; f++) {
    const dP_prime = P_prime[f] - P_prime[f - 1];
    pipe.faces[f].mdot -= d_f[f] * dP_prime;
  }

  // Boundary face corrections.
  // For fixed-P boundaries (Dirichlet): P'_boundary = 0, so correction uses P'[adjacent cell].
  // For free boundaries: correction is proportional to P'[boundary cell].
  if (n > 0) {
    if (P_left !== null) {
      // Fixed P at left: P'_left = 0, so mdot'[0] = -d_f[0] * (P'[0] - 0)
      pipe.faces[0].mdot -= d_f[0] * P_prime[0];
    } else {
      pipe.faces[0].mdot -= d_f[0] * P_prime[0];
    }
    if (P_right !== null) {
      // Fixed P at right: P'_right = 0, so mdot'[n] = -d_f[n] * (0 - P'[n-1])
      pipe.faces[n].mdot += d_f[n] * P_prime[n - 1];
    } else {
      pipe.faces[n].mdot += d_f[n] * P_prime[n - 1];
    }
  }

  return maxResidual;
}
